import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db, auth } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import useErrorReportText from './errorReportText';
import { ALL_STATUSES, REPORT_FILTER_KEYS } from './errorReportColumns';

const DAY_MS = 24 * 60 * 60 * 1000;
const EMPTY_FILTERS = Object.fromEntries(REPORT_FILTER_KEYS.map((key) => [key, '']));

const includesText = (value, term) => Boolean(value) && String(value).toLowerCase().includes(term.toLowerCase());

/**
 * Data of Çalışan Hata Geribildirimleri: the ErrorFeedbacks reports with the
 * reporter's name, the status change (who changed it and when), the Excel
 * export and the list filters. Firestore reads/writes, the name lookups and
 * the Excel layout are the old page's, unchanged.
 */
const useErrorReports = () => {
  const { userProfile } = useUserProfile();
  const { label } = useErrorReportText();
  const [isLoading, setIsLoading] = useState(true);
  const [errorReports, setErrorReports] = useState([]);
  const [selectedErrorReport, setSelectedErrorReport] = useState(null);
  const [searchTerms, setSearchTerms] = useState(EMPTY_FILTERS);
  const [generalSearchTerm, setGeneralSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [onlyScreenshots, setOnlyScreenshots] = useState(false);
  const [onlyRecent, setOnlyRecent] = useState(false);
  const [savingStatusId, setSavingStatusId] = useState(null);

  useEffect(() => {
    const fetchErrorReports = async () => {
      try {
        const errorReportsCollection = collection(db, 'ErrorFeedbacks');
        const errorReportsSnapshot = await getDocs(errorReportsCollection);

        const reports = await Promise.all(
          errorReportsSnapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();

            let userName = '';
            let userFound = false;

            // First check if userName is already in the error report
            if (data.userName && data.userName.trim()) {
              userName = data.userName.trim();
              userFound = true;
            }

            // If not found, try to fetch from Users/Employees
            if (!userFound) {
              try {
                // Try Users collection
                const userDocRef = doc(db, 'Users', data.uid);
                const userDocSnapshot = await getDoc(userDocRef);

                if (userDocSnapshot.exists()) {
                  const userData = userDocSnapshot.data();

                  const firstName = userData.firstName || userData.name || userData.eName ||
                                   userData.first_name || userData.givenName || '';
                  const lastName = userData.lastName || userData.surname || userData.eSurname ||
                                  userData.last_name || userData.familyName || '';
                  const fullName = userData.displayName || userData.fullName || userData.username || '';

                  userName = fullName.trim() || `${firstName} ${lastName}`.trim();

                  if (userName) {
                    userFound = true;
                  }
                }

                // If not found in Users or name is empty, try Employees collection
                if (!userFound) {
                  const employeeDocRef = doc(db, 'Employees', data.uid);
                  const employeeDocSnapshot = await getDoc(employeeDocRef);

                  if (employeeDocSnapshot.exists()) {
                    const employeeData = employeeDocSnapshot.data();

                    const eName = employeeData.eName || employeeData.name || employeeData.firstName ||
                                 employeeData.first_name || '';
                    const eSurname = employeeData.eSurname || employeeData.surname || employeeData.lastName ||
                                    employeeData.last_name || '';
                    const fullName = employeeData.displayName || employeeData.fullName || '';

                    userName = fullName.trim() || `${eName} ${eSurname}`.trim();

                    if (userName) {
                      userFound = true;
                    }
                  }
                }

                // If still not found, use UID as fallback
                if (!userFound || !userName) {
                  userName = `Eski Kullanıcı (${data.uid.substring(0, 8)}...)`;
                }
              } catch (error) {
                console.error(`Error fetching user data for uid ${data.uid}:`, error);
                userName = `Eski Kullanıcı (${data.uid.substring(0, 8)}...)`;
              }
            }

            return {
              id: docSnapshot.id,
              ...data,
              userName: userName,
              status: data.status || 'Beklemede',
              statusUpdatedBy: data.statusUpdatedBy || null,
              statusUpdatedAt: data.statusUpdatedAt || null,
              statusUpdatedByName: data.statusUpdatedByName || null,
              deviceInfo: data.deviceInfo || 'Unknown Device',
              browserInfo: data.browserInfo || 'Unknown Browser',
              errorDetails: data.errorDetails || 'No detailed information'
            };
          })
        );
        setErrorReports(reports);
      } catch (error) {
        console.error('Error fetching error reports: ', error);
        toast.error(label('toast.loadError'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchErrorReports();
    // Yalnızca açılışta okunur (eski sayfadaki gibi)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  // Genel arama, sütun süzgeçleri, durum ve hızlı süzgeçler birlikte uygulanır
  const filteredReports = useMemo(() => {
    const recentSince = Date.now() - DAY_MS;
    return errorReports
      .filter((report) => {
        if (generalSearchTerm) {
          const matches = includesText(report.errorSummary, generalSearchTerm)
            || includesText(report.uid, generalSearchTerm)
            || includesText(report.userName, generalSearchTerm);
          if (!matches) return false;
        }
        if (!REPORT_FILTER_KEYS.every((key) => !searchTerms[key] || includesText(report[key], searchTerms[key]))) return false;
        if (statusFilter !== ALL_STATUSES && report.status !== statusFilter) return false;
        if (onlyScreenshots && !report.screenshotUrl) return false;
        if (onlyRecent && !(new Date(report.timestamp).getTime() > recentSince)) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortOrder === 'asc') {
          return new Date(a.timestamp) - new Date(b.timestamp);
        }
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
  }, [errorReports, generalSearchTerm, searchTerms, statusFilter, onlyScreenshots, onlyRecent, sortOrder]);

  const handleFilter = useCallback((column, value) => {
    setSearchTerms((current) => ({ ...current, [column]: value }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchTerms(EMPTY_FILTERS);
    setGeneralSearchTerm('');
    setStatusFilter(ALL_STATUSES);
    setOnlyScreenshots(false);
    setOnlyRecent(false);
  }, []);

  const handleStatusChange = async (reportId, newStatus) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error(label('toast.notSignedIn'));
        return;
      }

      // Get current user's name - use userProfile context first if available
      let userName = '';

      // First try to use the userProfile context (most reliable)
      if (userProfile && userProfile.eName && userProfile.eSurname) {
        userName = `${userProfile.eName} ${userProfile.eSurname}`.trim();
      } else if (userProfile && userProfile.displayName) {
        userName = userProfile.displayName.trim();
      }

      // If not available from context, fetch from database
      if (!userName) {
        try {
          // Try Users collection first
          const userDocRef = doc(db, 'Users', currentUser.uid);
          const userDocSnapshot = await getDoc(userDocRef);

          if (userDocSnapshot.exists()) {
            const userData = userDocSnapshot.data();

            // Try all possible name field combinations
            if (userData.displayName && userData.displayName.trim()) {
              userName = userData.displayName.trim();
            } else if (userData.fullName && userData.fullName.trim()) {
              userName = userData.fullName.trim();
            } else if (userData.username && userData.username.trim()) {
              userName = userData.username.trim();
            } else {
              const firstName = userData.firstName || userData.name || userData.eName ||
                               userData.first_name || userData.givenName || '';
              const lastName = userData.lastName || userData.surname || userData.eSurname ||
                              userData.last_name || userData.familyName || '';

              if (firstName || lastName) {
                userName = `${firstName} ${lastName}`.trim();
              }
            }
          }

          // If not found in Users, try Employees collection
          if (!userName) {
            const employeeDocRef = doc(db, 'Employees', currentUser.uid);
            const employeeDocSnapshot = await getDoc(employeeDocRef);

            if (employeeDocSnapshot.exists()) {
              const employeeData = employeeDocSnapshot.data();

              // Try all possible name field combinations
              if (employeeData.displayName && employeeData.displayName.trim()) {
                userName = employeeData.displayName.trim();
              } else if (employeeData.fullName && employeeData.fullName.trim()) {
                userName = employeeData.fullName.trim();
              } else {
                const eName = employeeData.eName || employeeData.name || employeeData.firstName ||
                             employeeData.first_name || '';
                const eSurname = employeeData.eSurname || employeeData.surname || employeeData.lastName ||
                                employeeData.last_name || '';

                if (eName || eSurname) {
                  userName = `${eName} ${eSurname}`.trim();
                }
              }
            }
          }

          // Final fallback
          if (!userName) {
            userName = currentUser.displayName || currentUser.email || `Kullanıcı (${currentUser.uid.substring(0, 8)}...)`;
          }
        } catch (error) {
          console.error('Error fetching user name:', error);
          userName = currentUser.displayName || currentUser.email || `Kullanıcı (${currentUser.uid.substring(0, 8)}...)`;
        }
      }

      const reportRef = doc(db, 'ErrorFeedbacks', reportId);
      const updateData = {
        status: newStatus,
        statusUpdatedBy: currentUser.uid,
        statusUpdatedByName: userName,
        statusUpdatedAt: new Date().toISOString()
      };

      await updateDoc(reportRef, updateData);

      // Update local state
      setErrorReports(prevReports =>
        prevReports.map(report =>
          report.id === reportId
            ? { ...report, ...updateData }
            : report
        )
      );

      // Update selected report if it's the one being updated
      if (selectedErrorReport && selectedErrorReport.id === reportId) {
        setSelectedErrorReport(prev => ({ ...prev, ...updateData }));
      }

      toast.success(label('toast.statusUpdated'));
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(label('toast.statusError'));
    }
  };

  /** handleStatusChange with a busy flag for the control that started it. */
  const changeStatus = async (reportId, newStatus) => {
    if (savingStatusId) return;
    setSavingStatusId(reportId);
    try {
      await handleStatusChange(reportId, newStatus);
    } finally {
      setSavingStatusId(null);
    }
  };

  const downloadExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Error Reports');

      worksheet.columns = [
        { header: 'Error Summary', key: 'errorSummary', width: 30 },
        { header: 'Date', key: 'timestamp', width: 20 },
        { header: 'User ID', key: 'uid', width: 30 },
        { header: 'User Name', key: 'userName', width: 20 },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Status Updated By', key: 'statusUpdatedByName', width: 20 },
        { header: 'Has Screenshot', key: 'hasScreenshot', width: 15 },
        { header: 'Device Info', key: 'deviceInfo', width: 20 },
        { header: 'Browser Info', key: 'browserInfo', width: 20 },
      ];

      filteredReports.forEach(report => {
        worksheet.addRow({
          errorSummary: report.errorSummary || 'N/A',
          timestamp: new Date(report.timestamp).toLocaleString(),
          uid: report.uid || 'N/A',
          userName: report.userName || 'N/A',
          status: report.status || 'Beklemede',
          statusUpdatedByName: report.statusUpdatedByName || 'N/A',
          hasScreenshot: report.screenshotUrl ? 'Yes' : 'No',
          deviceInfo: report.deviceInfo || 'N/A',
          browserInfo: report.browserInfo || 'N/A',
        });
      });

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'error_reports.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(label('toast.excelDone'));
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error(label('toast.excelError'));
    }
  };

  // Statistics for the tiles
  const stats = useMemo(() => {
    const yesterday = new Date().getTime() - DAY_MS;
    return {
      total: errorReports.length,
      pending: errorReports.filter((report) => report.status === 'Beklemede').length,
      withScreenshots: errorReports.filter((report) => report.screenshotUrl).length,
      last24Hours: errorReports.filter((report) => new Date(report.timestamp).getTime() > yesterday).length,
    };
  }, [errorReports]);

  return {
    isLoading,
    errorReports,
    filteredReports,
    stats,
    selectedErrorReport,
    setSelectedErrorReport,
    searchTerms,
    handleFilter,
    generalSearchTerm,
    setGeneralSearchTerm,
    statusFilter,
    setStatusFilter,
    onlyScreenshots,
    setOnlyScreenshots,
    onlyRecent,
    setOnlyRecent,
    sortOrder,
    toggleSortOrder,
    clearAllFilters,
    changeStatus,
    savingStatusId,
    downloadExcel,
  };
};

export default useErrorReports;
